/**
 * Filters Store
 * Manages shared filter state for landing page
 * Separated from monolithic store following SOLID principles
 */

import { create } from 'zustand';
import { globalConfig } from '../config/globalConfig';
import type { FiltersStore } from '../interfaces/storeInterfaces/filtersStore';

export const useFiltersStore = create<FiltersStore>((set) => ({
  // Initial State
  selectedCountryIsoCode: null,
  selectedGradeId: null,
  selectedLanguageIsoCode: null,
  selectedSubjectId: null,
  selectedDisciplineId: null,
  selectedPendingGraphics: null,
  searchText: null,
  searchType: 'JobId',
  currentPage: globalConfig.defaultPageNumber,
  pageSize: globalConfig.defaultPageSize,

  // Actions
  setSelectedCountryIsoCode: (countryIsoCode) =>
    set({
      selectedCountryIsoCode: countryIsoCode,
      selectedGradeId: null,
      selectedLanguageIsoCode: null,
      selectedSubjectId: null,
      selectedDisciplineId: null, // Clear discipline when country/grade/language/subject path is used
      currentPage: globalConfig.defaultPageNumber,
    }),

  setSelectedGradeId: (gradeId) =>
    set({
      selectedGradeId: gradeId,
      selectedLanguageIsoCode: null,
      selectedSubjectId: null,
      selectedDisciplineId: null,
      currentPage: globalConfig.defaultPageNumber,
    }),

  setSelectedLanguageIsoCode: (languageIsoCode) =>
    set({
      selectedLanguageIsoCode: languageIsoCode,
      selectedSubjectId: null,
      selectedDisciplineId: null,
      currentPage: globalConfig.defaultPageNumber,
    }),

  setSelectedSubjectId: (subjectId) =>
    set({
      selectedSubjectId: subjectId,
      selectedDisciplineId: null,
      currentPage: globalConfig.defaultPageNumber,
    }),

  setSelectedDisciplineId: (disciplineId) =>
    set({
      selectedDisciplineId: disciplineId,
      selectedCountryIsoCode: null, // Clear country/grade/language/subject when discipline is used
      selectedGradeId: null,
      selectedLanguageIsoCode: null,
      selectedSubjectId: null,
      currentPage: globalConfig.defaultPageNumber,
    }),

  setSelectedPendingGraphics: (pendingGraphics) =>
    set({
      selectedPendingGraphics: pendingGraphics,
      currentPage: globalConfig.defaultPageNumber,
    }),

  setSearchText: (searchtext) =>
    set({
      searchText: searchtext,
      currentPage: globalConfig.defaultPageNumber,
    }),

  setSearchType: (searchType) =>
    set({
      searchType: searchType,
    }),

  setCurrentPage: (page) => set({ currentPage: page }),

  setPageSize: (size) =>
    set({
      pageSize: size,
      currentPage: globalConfig.defaultPageNumber,
    }),

  resetFilters: () =>
    set({
      selectedCountryIsoCode: null,
      selectedGradeId: null,
      selectedLanguageIsoCode: null,
      selectedSubjectId: null,
      selectedDisciplineId: null,
      selectedPendingGraphics: null,
      searchText: null,
      searchType: 'JobId',
      currentPage: globalConfig.defaultPageNumber,
    }),
}));
